import styled from 'styled-components'
import { SPACING, BORDER_RADIUS, COLORS, TYPOGRAPHY, FONT_WEIGHT } from '../../../styles/theme'

// ─── Page layout ─────────────────────────────────────────────────────────────

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${SPACING.xl};
  color: var(--color-text-primary);
`

export const Content = styled.div`
  width: 100%;
  max-width: 960px;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-bottom: 100px;
`

export const Header = styled.div`
  margin-bottom: ${SPACING.xxl};
`

export const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  margin-bottom: ${SPACING.lg};
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

export const Meta = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin: 0;
`

// ─── Question card ────────────────────────────────────────────────────────────

export const QuestionCard = styled.div<{ $answered: boolean }>`
  background: var(--color-bg-card);
  border: 1px solid
    ${({ $answered }) => ($answered ? 'var(--color-primary)' : 'var(--color-border)')};
  border-radius: ${BORDER_RADIUS.xl};
  padding: ${SPACING.xl};
  margin-bottom: ${SPACING.md};
  transition: border-color 0.2s ease;
`

export const QuestionMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${SPACING.lg};
`

export const QuestionIndex = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const AnsweredDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-primary);
  opacity: 0.7;
`

// ─── Submit section ───────────────────────────────────────────────────────────

export const SubmitSection = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: color-mix(in srgb, var(--color-bg-primary) 85%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid var(--color-border);
  padding: ${SPACING.md} ${SPACING.xl};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.xs};
`

export const SubmitSectionInner = styled.div`
  width: 100%;
  max-width: 960px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const ProgressText = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin: 0;
`

export const SubmitButton = styled.button<{ $disabled: boolean }>`
  width: 100%;
  height: 48px;
  border-radius: ${BORDER_RADIUS.lg};
  border: none;
  background: ${({ $disabled }) => ($disabled ? 'var(--color-border-dark)' : COLORS.primary)};
  color: ${({ $disabled }) => ($disabled ? 'var(--color-text-tertiary)' : '#ffffff')};
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition:
    background 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: ${COLORS.primaryHover};
  }
`

// ─── Thank you / closed states ────────────────────────────────────────────────

export const StateCard = styled.div`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.xl};
  padding: ${SPACING.xxxl} ${SPACING.xl};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.md};
`

export const StateTitle = styled.h2`
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  margin: 0;
  color: var(--color-text-primary);
`

export const StateBody = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
`

// ─── Brand acquisition footer ─────────────────────────────────────────────────

export const BrandFooter = styled.div`
  margin-top: ${SPACING.xl};
  padding-top: ${SPACING.xl};
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.sm};
  text-align: center;
`

export const BrandFooterLabel = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
  margin: 0;
  letter-spacing: 0.3px;
`

export const BrandFooterHeadline = styled.p`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: 0;
`

export const BrandFooterSub = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.55;
  max-width: 340px;
`

export const BrandCTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  padding: 10px 22px;
  border-radius: ${BORDER_RADIUS.lg};
  background: ${COLORS.primary};
  color: #ffffff;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-decoration: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.88;
    color: #ffffff;
  }
`

export const PoweredBy = styled.p`
  margin-top: ${SPACING.xl};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
  text-align: center;
  margin-bottom: 0;

  a {
    color: var(--color-text-tertiary);
    text-decoration: none;
    font-weight: ${FONT_WEIGHT.semibold};

    &:hover {
      color: var(--color-text-secondary);
    }
  }
`

// ─── Loading / error ──────────────────────────────────────────────────────────

export const CenterBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  background: var(--color-bg-primary);
`

export const ErrorMessage = styled.p`
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.body.fontSize};
  text-align: center;
`
